import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import { schemaLabel } from '../../utils/schemaUtils';
import { GroupFaceIcon, PersonFace } from '../../components/nucleus/faces';
import { Empty } from '../../components/nucleus/Empty';
import { TitledSet } from '../../components/nucleus/SetHead';
import DataTable from '../../components/nucleus/DataTable';

// each public entry and its kind
const titleOf = (n) => n.title || n.value_json?.title || 'Untitled';
const kindOf = (n) => (n.schema_name ? schemaLabel(n.schema_name) : null);
const COLUMNS = [
  { id: 'entry', label: 'Entry', lock: true, min: 180, grow: 2, value: titleOf, cell: titleOf },
  { id: 'kind', label: 'Kind', min: 100, grow: 1, value: kindOf, cell: (n) => kindOf(n) || '—' },
];

/**
 * GroupPublicView — the viewer side of a group's public page. Any logged-in user can see the
 * nodes the group exposes via its public-profile EP (GET /groups/:id/public). Read-only.
 * With no group in the address (/me/public) it is YOUR public page, as it reads
 * (GET /users/me/public-profile/view) — what "View the page" opens.
 */
export default function GroupPublicView() {
  const { groupId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const resp = groupId ? await api.getGroupPublicView(groupId) : await api.getMyPublicView();
        if (alive) setData(resp);
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load public page');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [groupId]);

  // loading draws nothing; a failure is the one empty line, said in red
  if (loading) return null;
  if (error) return <Empty error>{error}</Empty>;

  const g = (groupId ? data?.group : data?.user) || {};
  const nodes = data?.nodes || [];

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '32px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '0 var(--nx-gutter, 28px) 22px' }}>
        {groupId ? <GroupFaceIcon url={g.avatar_url || undefined} size={48} /> : <PersonFace url={g.avatar_url || undefined} size={48} />}
        <div>
          <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.01em' }}>{g.name || (groupId ? 'Group' : 'You')}</h1>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Public page{g.description ? ` · ${g.description}` : ''}</div>
        </div>
      </div>

      {!data?.is_public ? (
        <Empty>{groupId ? 'This group hasn’t published a public page.' : 'You haven’t made a public page.'}</Empty>
      ) : nodes.length === 0 ? (
        <Empty>Nothing public yet.</Empty>
      ) : (
        <TitledSet title={nodes.length === 1 ? 'Public entry' : 'Public entries'} count={nodes.length} style={{ padding: '0 var(--nx-gutter, 28px) 48px', boxSizing: 'border-box' }}>
          <DataTable id="public-view-entries" label="Public entries" columns={COLUMNS} rows={nodes} keyOf={(n) => n.id} />
        </TitledSet>
      )}
    </div>
  );
}
